// Pure decision logic for the subscription "dunning" banner (past_due /
// pending_cancellation). Extracted from SubscriptionBanner.tsx so the
// platform split, message assembly, and status_url fallback can be tested in
// vitest's node environment without rendering React.
//
// See the test suite for the full platform × status × provider × props matrix.

import { getStoreManageUrl, STORE_MANAGE_LABEL } from "@/lib/storeSubscriptionLinks";
import { hebrewDaysPhrase } from "@/lib/hebrewPlural";
import { IN_APP_CONTINUE_LABEL, IN_APP_PAYWALL_PATH } from "@/lib/inAppSubscribe";

export type DunningPlatform = "ios" | "android" | "web";

export interface DunningBannerInput {
  subscriptionStatus?: string | null;
  subscriptionDaysRemaining?: number | null;
  subscriptionRetryCount?: number | null;
  subscriptionMaxRetries?: number | null;
  subscriptionDeclineType?: string | null;
  subscriptionStatusUrl?: string | null;
  /**
   * profiles.subscription_provider — 'apple' | 'google' (store-billed),
   * 'payplus' | 'cardcom' | null (web-billed). Only consulted on native.
   */
  subscriptionProvider?: string | null;
  /** Capacitor.getPlatform() — "ios" | "android" | "web". */
  platform: DunningPlatform;
  /**
   * Store purchases are live on this platform (app_config iap_enabled_*). Only
   * consulted on native, for a web-billed member with a failing card.
   */
  iapEnabled?: boolean;
  /**
   * Admin-edited message templates from `banner_config` (see
   * renderDunningTemplate). A missing or unusable template falls back to the
   * built-in wording, so an empty or broken row can never blank the banner.
   */
  messageOverrides?: DunningMessageOverrides;
}

/** `banner_config.banner_key` — one editable message per banner state. */
export type DunningMessageKey =
  | "past_due_soft"
  | "past_due_hard"
  | "on_hold"
  | "pending_cancellation";

export type DunningMessageOverrides = Partial<Record<DunningMessageKey, string | null>>;

/** The tokens an admin may use in a message template. */
export const DUNNING_TEMPLATE_TOKENS = ["retry_count", "max_retries", "when"] as const;
type DunningTemplateValues = Record<(typeof DUNNING_TEMPLATE_TOKENS)[number], string | null>;

/**
 * Fills `{token}` placeholders in an admin-edited message.
 *
 * A sentence whose token has no value for this member is dropped whole — that
 * is how the built-in wording behaves ("ניסיון 2/4." simply isn't shown when
 * the retry counters are unknown), and it keeps a half-filled sentence from
 * ever reaching a member.
 *
 * Returns null when the template is empty, renders to nothing, or names a
 * token that doesn't exist (e.g. the `{days_remaining}` of the original seed
 * rows, which produced "בעוד 1 ימים"). The caller then uses the built-in
 * wording.
 */
export function renderDunningTemplate(
  template: string | null | undefined,
  values: DunningTemplateValues,
): string | null {
  if (!template?.trim()) return null;
  // No lookbehind: it is a parse-time SyntaxError on older iOS WebViews.
  const sentences = template.match(/[^.!?]+[.!?]*\s*/g) ?? [];
  const kept: string[] = [];
  for (const sentence of sentences) {
    let missing = false;
    let unknown = false;
    const rendered = sentence.replace(/\{(\w+)\}/g, (_match, token: string) => {
      if (!(DUNNING_TEMPLATE_TOKENS as readonly string[]).includes(token)) {
        unknown = true;
        return "";
      }
      const value = values[token as keyof DunningTemplateValues];
      if (value == null) missing = true;
      return value ?? "";
    });
    if (unknown) return null;
    if (!missing) kept.push(rendered.trim());
  }
  return kept.join(" ").trim() || null;
}

export interface DunningBannerButton {
  label: string;
  href: string;
  /**
   * True when the button routes to WhatsApp support rather than a self-serve
   * management page — i.e. web with no stored status_url (the ~1,124 unmatched
   * pending_cancellation cohort). Lets the UI vary copy if desired; does not
   * change current rendering.
   */
  isSupportFallback: boolean;
  /** An in-app route (navigate), not an external URL (openExternalUrl). */
  internal?: boolean;
}

export interface DunningBannerView {
  variant: "past_due" | "on_hold" | "pending_cancellation";
  tone: "destructive" | "warning";
  message: string;
  /**
   * null when no CTA may be shown — a native build cannot offer a web-billed
   * member ANY billing destination (see getDunningBannerView). Callers must
   * render the message text-only in that case.
   */
  button: DunningBannerButton | null;
}

// WhatsApp support deep-link (same number as profile Settings).
export const WA_SUPPORT_PHONE = "972523415256";
export const waSupportUrl = (text: string) =>
  `https://wa.me/${WA_SUPPORT_PHONE}?text=${encodeURIComponent(text)}`;

const WA_MSG_PAYMENT =
  "היי, יש בעיה בתשלום של המנוי שלי ואני רוצה לעדכן אמצעי תשלום. אפשר לעזור?";
const WA_MSG_STAY =
  "היי, המנוי שלי לקראת סיום ואני רוצה להישאר. אפשר לעזור?";

/**
 * Maps subscription state + platform + provider to the banner view, or null when
 * no banner should render.
 *
 * Since IAP (2026-09-27): a native web-billed member whose card is FAILING
 * (past_due / on_hold) gets "המשך המנוי באפליקציה" → the in-app paywall. That is
 * a store purchase, so 3.1.1 allows it, and claim-iap's hub rescue retires the
 * failing web row, so it can't bill twice (see @/lib/inAppSubscribe).
 * pending_cancellation stays text-only on native: that web subscription is paid up
 * to its end date and the rescue won't touch it, so buying now would charge twice
 * for the same days. Once it lapses, the locked tabs lead to the paywall anyway.
 *
 * Button policy:
 *   web                          — unchanged. Prefer the stored status_url; fall
 *                                  back to WhatsApp support when it is null.
 *   native + store-billed        — the store's own subscription screen. That is
 *                                  where an Apple/Google member fixes a declined
 *                                  card or stops a cancellation, and 3.1.1
 *                                  explicitly permits linking to it.
 *   native + web-billed/unknown  — NO button. status_url leads to payment (3.1.1),
 *                                  and the WhatsApp fallback carried explicit
 *                                  "update your payment method" wording, which is
 *                                  a payment-leading link under Play's Payments
 *                                  policy. Neither survives review, so the banner
 *                                  goes text-only and the message still lands.
 */
export function getDunningBannerView(
  input: DunningBannerInput,
): DunningBannerView | null {
  const isNative = input.platform === "ios" || input.platform === "android";
  const storeManageUrl = getStoreManageUrl(input.subscriptionProvider);
  const overrides = input.messageOverrides ?? {};

  const resolveButton = (
    waMsg: string,
    label: string,
    inAppAllowed: boolean,
  ): DunningBannerButton | null => {
    if (isNative) {
      if (storeManageUrl) {
        return { label: STORE_MANAGE_LABEL, href: storeManageUrl, isSupportFallback: false };
      }
      return inAppAllowed && input.iapEnabled
        ? { label: IN_APP_CONTINUE_LABEL, href: IN_APP_PAYWALL_PATH, isSupportFallback: false, internal: true }
        : null;
    }
    const waHref = waSupportUrl(waMsg);
    const url = input.subscriptionStatusUrl ?? null;
    return {
      label,
      href: url ?? waHref,
      isSupportFallback: url == null,
    };
  };

  if (input.subscriptionStatus === "past_due") {
    const decline =
      input.subscriptionDeclineType === "hard"
        ? " (כרטיס לא תקין)"
        : " (בעיה זמנית)";
    const rc = input.subscriptionRetryCount;
    const mr = input.subscriptionMaxRetries;
    const dr = input.subscriptionDaysRemaining;
    const retryPart = rc != null && mr != null ? `ניסיון ${rc}/${mr}. ` : "";
    const daysPart =
      dr != null && dr > 0
        ? (dr === 1 ? "המנוי יבוטל מחר." : `המנוי יבוטל בעוד ${hebrewDaysPhrase(dr)}.`)
        : "";
    const override = renderDunningTemplate(
      overrides[input.subscriptionDeclineType === "hard" ? "past_due_hard" : "past_due_soft"],
      {
        retry_count: rc != null && mr != null ? String(rc) : null,
        max_retries: rc != null && mr != null ? String(mr) : null,
        when: dr != null && dr > 0 ? (dr === 1 ? "מחר" : `בעוד ${hebrewDaysPhrase(dr)}`) : null,
      },
    );
    return {
      variant: "past_due",
      tone: "destructive",
      message: override ?? `התשלום האחרון נכשל${decline}. ${retryPart}${daysPart}`,
      button: resolveButton(WA_MSG_PAYMENT, "עדכון תשלום", true),
    };
  }

  // on_hold = card failure past its retry window. Access is retained (see
  // userAccessService — full access, not a wall); this banner is the nudge to
  // fix payment. No retry/day counters here — the retries are already exhausted.
  if (input.subscriptionStatus === "on_hold") {
    return {
      variant: "on_hold",
      tone: "destructive",
      message:
        renderDunningTemplate(overrides.on_hold, { retry_count: null, max_retries: null, when: null }) ??
        "התשלום נכשל — עדכנו אמצעי תשלום כדי לא לאבד את הגישה למנוי.",
      button: resolveButton(WA_MSG_PAYMENT, "עדכון תשלום", true),
    };
  }

  if (input.subscriptionStatus === "pending_cancellation") {
    const dr = input.subscriptionDaysRemaining;
    const tail =
      dr == null ? " בקרוב"
        : dr <= 0 ? " היום"
        : dr === 1 ? " מחר"
        : ` בעוד ${hebrewDaysPhrase(dr)}`;
    return {
      variant: "pending_cancellation",
      tone: "warning",
      message:
        renderDunningTemplate(overrides.pending_cancellation, {
          retry_count: null,
          max_retries: null,
          when: tail.trim(),
        }) ?? `המנוי שלך מסתיים${tail}.`,
      // Controls don't address one gender ("רוצה" is masculine singular).
      button: resolveButton(WA_MSG_STAY, "להישאר במנוי", false),
    };
  }

  return null;
}
